// API client for the control plane admin surface.
//
// This app is deployed separately from the control plane it manages (see
// ../README.md) — every call here is cross-origin, not same-origin the
// way it would be if this were still served by the server it talks to.
// `server` injects `window.__CLOUDISH_API_URL__` into the served
// index.html at container start, read from that deployment's own
// CLOUDISH_API_URL env var, so one built image works against any environment
// (next vs. devel) without a separate build per target. Empty/unset means
// "same origin" — the default local-dev shape (`npm run dev`, proxied by
// vite.config.ts) still works with zero configuration.

declare global {
  interface Window {
    __CLOUDISH_API_URL__?: string;
  }
}

export function apiBase(): string {
  return (typeof window !== 'undefined' && window.__CLOUDISH_API_URL__) || '';
}

let tokenProvider: () => Promise<string | null> = async () => null;

export function setTokenProvider(provider: () => Promise<string | null>) {
  tokenProvider = provider;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  payload: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.code = (payload as { code?: string } | undefined)?.code;
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await tokenProvider();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function request<T>(
  path: string,
  options: RequestInit & { raw?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.body && !(options.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
    ...(await authHeaders()),
    ...((options.headers as Record<string, string>) || {}),
  };

  // A bearer token, never a cookie: the control plane's CORS doesn't allow
  // credentials, so a request that asked for them would be refused.
  const response = await fetch(`${apiBase()}${path}`, { ...options, headers, credentials: 'omit' });

  if (options.raw) return response as unknown as T;

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!response.ok) {
    const message =
      (payload as { error?: string })?.error || (typeof payload === 'string' ? payload : response.statusText);
    throw new ApiError(message || 'request failed', response.status, payload);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};

export default api;
